/**
 * Meta Cloud API — Facebook SDK Configuration
 *
 * Uses FB.login() popup (Embedded Signup) instead of redirect-based OAuth.
 * The config_id comes from Meta's WhatsApp Embedded Signup configuration.
 *
 * META_APP_ID and META_CONFIG_ID are defined per environment
 * in the vite.config.{local|dev|prod}.ts `define` blocks,
 * following the same pattern as URL_API_PLICK, FIREBASE_API_KEY, etc.
 *
 * Coexistence: el negocio conecta el número que YA usa en la app de WhatsApp
 * Business. Se activa pasando `extras.featureType = 'whatsapp_business_app_onboarding'`.
 * En ese flujo el popup reemplaza la selección de WABA por una pantalla de "conectar
 * cuenta existente" (código de verificación en la app) y el postMessage de éxito
 * (`event: FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`) trae SOLO `waba_id` — el
 * `phone_number_id` lo resuelve el backend con GET /{waba_id}/phone_numbers.
 *
 * See: https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users
 */

/* eslint-disable no-var */
declare const META_APP_ID: string;
declare const META_CONFIG_ID: string;
/* eslint-enable no-var */

const META_SDK_VERSION = 'v25.0';

/**
 * featureType que activa el flujo Coexistence del Embedded Signup.
 * Si se quisiera revertir al onboarding Cloud API estándar, basta con no pasar `extras`.
 */
export const COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding';

/** Datos del comercio en sesión que se usan para precargar el diálogo de Meta. */
export interface EmbeddedSignupBusinessPrefill {
  name?: string;
  email?: string;
  website?: string;
  /** Tal como lo guarda el comercio: long_name de Google Places ('México'), no ISO-2. */
  country?: string;
}

/** Forma exacta que Meta espera en `extras.setup.business` (paridad con el Embedded Signup de Kapso). */
interface MetaSetupBusiness {
  name: string;
  email: string;
  website: string;
  address: { country: string };
}

/**
 * `address.country` del comercio se guarda como long_name de Google Places ('Mexico',
 * 'México') o con el default literal 'Mexico'; Meta espera ISO 3166-1 alpha-2 ('MX').
 * Lo que no se pueda mapear se manda como '' — Meta deja el campo vacío para que el
 * comercio lo elija, que es mejor que enviarle un valor que va a descartar igual.
 */
const COUNTRY_ISO2_BY_NAME: Record<string, string> = {
  mexico: 'MX',
  méxico: 'MX',
  'estados unidos': 'US',
  'united states': 'US',
  'estados unidos de américa': 'US',
  'united states of america': 'US',
};

export const toIso2Country = (country?: string): string => {
  const raw = (country || '').trim();
  if (!raw) return '';
  if (/^[a-z]{2}$/i.test(raw)) return raw.toUpperCase();
  return COUNTRY_ISO2_BY_NAME[raw.toLowerCase()] || '';
};

/**
 * Construye `extras.setup.business`, el bloque que precarga la pantalla "Completa la
 * información de tu empresa" del diálogo de Meta. Sin él el comercio teclea todo a mano.
 *
 * Todas las claves salen SIEMPRE presentes y como string: un `undefined` se pierde al
 * serializar el objeto hacia el popup y dejaría el bloque incompleto.
 *
 * @param {EmbeddedSignupBusinessPrefill} [business] Datos del comercio en sesión.
 * @returns {MetaSetupBusiness} Bloque listo para `extras.setup.business`.
 */
export const buildSetupBusiness = (business?: EmbeddedSignupBusinessPrefill): MetaSetupBusiness => ({
  name: business?.name?.trim() || '',
  email: business?.email?.trim() || '',
  website: business?.website?.trim() || '',
  address: { country: toIso2Country(business?.country) },
});

/** Resultado del Embedded Signup: el code más los identificadores capturados del postMessage. */
export interface EmbeddedSignupResult {
  code: string;
  wabaId?: string;
  phoneNumberId?: string;
  featureType: string;
  finishEvent?: string;
}

/** Estructura del `message` event que Meta emite durante el Embedded Signup. */
interface WaEmbeddedSignupMessage {
  type: 'WA_EMBEDDED_SIGNUP';
  event?: string;
  version?: number;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
    current_step?: string;
    error_message?: string;
    error_code?: string;
  };
}

/**
 * QUÉ EMITE METE Y QUÉ NO — verificado en la documentación oficial, porque de esto
 * depende qué puede decir la tarjeta con honestidad mientras el popup está abierto.
 *
 * Meta emite SOLO eventos TERMINALES. No hay eventos de paso intermedio: no existe un
 * "el comercio va en la pantalla de verificación". Los `event` documentados son
 * `FINISH`, `FINISH_ONLY_WABA`, `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`,
 * `FINISH_OBO_MIGRATION`, `FINISH_GRANT_ONLY_API_ACCESS`, `ERROR` y `CANCEL`.
 *   https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/implementation
 *
 * Consecuencia de diseño: el progreso fino NO se puede inventar del lado nuestro y
 * tampoco lo regala Meta. Lo único honesto son dos fases, y las dos las conocemos sin
 * ayuda: (1) el popup está abierto y esperamos a Meta, (2) ya tenemos el `code` y
 * estamos canjeándolo en nuestro backend.
 *
 * TRAMPA DOCUMENTADA: el reporte de ERROR viaja con `event: 'CANCEL'`, no con
 * `'ERROR'`. El bloque JSON oficial del error literalmente dice `event: 'CANCEL'` con
 * `error_message`/`error_code` adentro. Lo que separa "el comercio se salió" de "Meta
 * falló" NO es el `event`, es si el `data` trae `error_message`. Discriminar por
 * `event` daría el diagnóstico al revés en uno de los dos casos.
 *   https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/errors/
 */

/** Pantallas en las que Meta reporta abandono (`data.current_step` del evento CANCEL). */
const CURRENT_STEP_LABELS: Record<string, string> = {
  BUSINESS_ACCOUNT_SELECTION: 'la selección de la cuenta de negocio',
  WABA_PHONE_PROFILE_PICKER: 'la selección del número y el perfil',
  WHATSAPP_BUSINESS_PROFILE_SETUP: 'la configuración del perfil de WhatsApp Business',
  PHONE_NUMBER_SETUP: 'el alta del número',
  PHONE_NUMBER_VERIFICATION: 'la verificación del número',
  PERMISSIONS: 'la pantalla de permisos',
};

/**
 * Traduce el `current_step` del abandono a algo que el comercio reconozca de haber
 * visto. Un `PHONE_NUMBER_VERIFICATION` crudo no le dice nada; "la verificación del
 * número" le dice dónde retomar.
 *
 * @param {string} [currentStep] Valor crudo de `data.current_step`.
 * @returns {string} Frase en español, o cadena vacía si el paso es desconocido/ausente.
 */
export const getSignupStepLabel = (currentStep?: string): string =>
  currentStep ? CURRENT_STEP_LABELS[currentStep] || '' : '';

/**
 * Desenlace del Embedded Signup. Es una unión discriminada y no un `resultado | null`
 * porque "el comercio cerró el diálogo" y "Meta rechazó algo" terminaban las dos en
 * `null` y la tarjeta no podía distinguirlas: las dos se veían igual, o sea como nada.
 */
export type EmbeddedSignupOutcome =
  | ({ status: 'COMPLETED' } & EmbeddedSignupResult)
  | { status: 'CANCELLED'; currentStep?: string }
  | { status: 'ERROR'; errorMessage?: string; errorCode?: string };

/**
 * Load the Facebook SDK asynchronously.
 * Idempotent: if already loaded, resolves immediately.
 * @returns {Promise<void>} Resolves when SDK is ready
 */
export const loadFacebookSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Init callback
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });
      resolve();
    };

    // Inject SDK script if not already present
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Launch the Meta Embedded Signup popup via FB.login() en modo Coexistence.
 *
 * Captura el `waba_id` (y `phone_number_id` si el flujo lo provee) vía el
 * `message` event de Meta y lo combina con el auth code del callback de FB.login.
 *
 * @param {EmbeddedSignupBusinessPrefill} [business] Datos del comercio en sesión que precargan el
 *   formulario de Meta. Opcional: sin él el bloque viaja igual pero con strings vacíos.
 * @returns {Promise<EmbeddedSignupOutcome>} Desenlace: completado, cancelado o error de Meta.
 */
export const launchEmbeddedSignup = (business?: EmbeddedSignupBusinessPrefill): Promise<EmbeddedSignupOutcome> => {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    // El postMessage de Coexistence llega ANTES del callback de FB.login y trae solo
    // `waba_id`; lo guardamos para fusionarlo con el `code` cuando resuelva el login.
    let sessionInfo: { wabaId?: string; phoneNumberId?: string; finishEvent?: string } = {};

    // Lo que Meta dijo al salirse (abandono o error). Se guarda para poder decirle al
    // comercio QUÉ pasó, en vez del `null` mudo de antes.
    let abortOutcome: EmbeddedSignupOutcome | null = null;

    // La promesa se resuelve por dos caminos que pueden dispararse los dos (el
    // postMessage de CANCEL y luego el callback de FB.login): el primero manda.
    let settled = false;

    // Validar el origen por hostname exacto, no por sufijo: `endsWith('facebook.com')`
    // dejaría pasar `https://evilfacebook.com`. Aceptamos facebook.com y sus subdominios.
    const isFacebookOrigin = (origin: string): boolean => {
      try {
        const { protocol, hostname } = new URL(origin);
        return protocol === 'https:' && (hostname === 'facebook.com' || hostname.endsWith('.facebook.com'));
      } catch {
        return false;
      }
    };

    let cleanup = () => undefined as void;

    const settle = (outcome: EmbeddedSignupOutcome) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(outcome);
    };

    const sessionInfoListener = (event: MessageEvent) => {
      if (!isFacebookOrigin(event.origin)) return;
      try {
        const message = JSON.parse(event.data) as WaEmbeddedSignupMessage;
        if (message.type !== 'WA_EMBEDDED_SIGNUP') return;

        const data = message.data || {};

        // Salida sin éxito. El discriminador es `error_message`, NO el `event`: Meta
        // reporta sus errores con `event: 'CANCEL'` (ver el bloque de arriba).
        if (message.event === 'CANCEL' || message.event === 'ERROR') {
          abortOutcome = data.error_message
            ? { status: 'ERROR', errorMessage: data.error_message, errorCode: data.error_code }
            : { status: 'CANCELLED', currentStep: data.current_step };

          // Se resuelve YA, sin esperar al callback de FB.login. Ese callback puede no
          // llegar nunca si el comercio cierra la ventana a lo bruto, y entonces la
          // promesa quedaba colgada para siempre — con la tarjeta clavada en
          // "Conectando…" hasta que el comercio recargara la página.
          settle(abortOutcome);
          return;
        }

        // Evento de finalización: NO resuelve. En Coexistence este postMessage llega
        // ANTES del callback, y sin el `code` del callback no hay nada que canjear.
        sessionInfo = {
          wabaId: data.waba_id,
          phoneNumberId: data.phone_number_id,
          finishEvent: message.event,
        };
      } catch {
        // Mensaje no-JSON o ajeno al Embedded Signup — ignorar.
      }
    };

    window.addEventListener('message', sessionInfoListener);
    cleanup = () => window.removeEventListener('message', sessionInfoListener);

    // Igualado al Embedded Signup de Kapso (Tech Provider que sí lanza coexistence):
    // `featureType` va emparejado con `sessionInfoVersion:'3'` + `display:'popup'`.
    // Sin `sessionInfoVersion` Meta puede ignorar el featureType y caer al flujo estándar.
    const loginOptions = {
      config_id: META_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      display: 'popup',
      extras: {
        // `setup.business` precarga la pantalla "Completa la información de tu empresa".
        // Sin este bloque el comercio teclea nombre, correo, sitio y país a mano.
        setup: { business: buildSetupBusiness(business) },
        sessionInfoVersion: '3',
        featureType: COEXISTENCE_FEATURE_TYPE,
      },
    };

    window.FB.login((response: { authResponse?: { code?: string } }) => {
      const code = response.authResponse?.code;
      if (code) {
        settle({
          status: 'COMPLETED',
          code,
          wabaId: sessionInfo.wabaId,
          phoneNumberId: sessionInfo.phoneNumberId,
          featureType: COEXISTENCE_FEATURE_TYPE,
          finishEvent: sessionInfo.finishEvent,
        });
        return;
      }

      // Callback sin `code`: el comercio cerró el diálogo. Si Meta ya nos había dicho
      // POR QUÉ (abandono con `current_step`, o error con `error_message`), ese
      // diagnóstico manda; si no llegó nada, al menos sabemos que fue cancelación.
      settle(abortOutcome || { status: 'CANCELLED' });
    }, loginOptions);
  });
};

// Extend window for Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          display?: string;
          extras?: {
            setup?: { business?: MetaSetupBusiness } & Record<string, unknown>;
            featureType?: string;
            sessionInfoVersion?: string;
          };
        },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}
